import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight } from 'lucide-react';

/**
 * MobileServicesNav - Systems-led navigation for mobile
 * 
 * Design philosophy:
 * - Growth stage cards, not service lists
 * - One stage expanded at a time
 * - Calm, executive-friendly UX
 * - No icons, no gradients, typography-driven
 */

// Check for reduced motion preference
const useReducedMotion = () => {
  const [reducedMotion, setReducedMotion] = useState(false);
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);
    
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);
  
  return reducedMotion;
};

interface GrowthStage {
  id: string;
  label: string;
  tagline: string;
  services: { label: string; href: string }[];
}

const growthStages: GrowthStage[] = [
  {
    id: 'acquire',
    label: 'Acquire',
    tagline: 'Intent-led customer acquisition',
    services: [
      { label: 'Paid Search', href: '/paid-search' },
      { label: 'Paid Social', href: '/paid-social' },
      { label: 'Programmatic', href: '/programmatic' },
      { label: 'SEO', href: '/seo' },
    ],
  },
  {
    id: 'convert',
    label: 'Convert',
    tagline: 'Conversion and experience optimization',
    services: [
      { label: 'CRO', href: '/cro' },
      { label: 'Landing Pages', href: '/landing-pages' },
      { label: 'Web Development', href: '/web-development' },
      { label: 'UX Strategy', href: '/ux-strategy' },
      { label: 'Creative', href: '/creative' },
    ],
  },
  {
    id: 'retain',
    label: 'Retain',
    tagline: 'Lifecycle and customer value growth',
    services: [
      { label: 'Email Marketing', href: '/email-marketing' },
      { label: 'Lifecycle Marketing', href: '/lifecycle-marketing' },
      { label: 'Loyalty Programs', href: '/loyalty-programs' },
      { label: 'CRM Strategy', href: '/crm-strategy' },
    ],
  },
  {
    id: 'scale',
    label: 'Scale',
    tagline: 'Analytics, infrastructure, and intelligence',
    services: [
      { label: 'Analytics', href: '/analytics' },
      { label: 'Data Management', href: '/data-management' },
      { label: 'AI Automation', href: '/ai-automation' },
      { label: 'Google Cloud Platform', href: '/google-cloud-platform' },
    ],
  },
];

interface MobileServicesNavProps {
  onNavigate: () => void;
}

const MobileServicesNav = ({ onNavigate }: MobileServicesNavProps) => {
  const [expandedStage, setExpandedStage] = useState<string | null>(null);
  const reducedMotion = useReducedMotion();

  const toggleStage = (stageId: string) => {
    setExpandedStage(prev => prev === stageId ? null : stageId);
  };

  const transitionClass = reducedMotion ? '' : 'transition-all duration-200 ease-out';

  return (
    <div className="space-y-1">
      {/* Intent Safety Net */}
      <Link
        to="/solutions"
        onClick={onNavigate}
        className="flex items-center gap-2 py-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <span>Not sure what you need?</span>
        <span className="text-foreground font-medium">View our growth systems</span>
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>

      <div className="h-px bg-border my-3" />

      {/* Growth Stage Cards */}
      <div className="space-y-1">
        {growthStages.map((stage) => {
          const isExpanded = expandedStage === stage.id;
          
          return (
            <div key={stage.id}>
              {/* Stage Card */}
              <button
                onClick={() => toggleStage(stage.id)}
                className={`
                  w-full flex items-center justify-between min-h-[56px] py-4 px-1
                  text-left ${reducedMotion ? '' : 'transition-colors duration-150'}
                  ${isExpanded ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}
                `}
                aria-expanded={isExpanded}
              >
                <div>
                  <span className="text-xs uppercase tracking-[0.15em] font-medium block">
                    {stage.label}
                  </span>
                  <span className={`
                    text-sm mt-0.5 block ${reducedMotion ? '' : 'transition-opacity duration-150'}
                    ${isExpanded ? 'text-muted-foreground' : 'text-muted-foreground/70'}
                  `}>
                    {stage.tagline}
                  </span>
                </div>
                <ChevronRight 
                  className={`
                    h-4 w-4 flex-shrink-0 ${reducedMotion ? '' : 'transition-transform duration-150'}
                    ${isExpanded ? 'rotate-90' : ''}
                  `} 
                />
              </button>

              {/* Services Panel - Animated */}
              <div
                className={`
                  overflow-hidden ${transitionClass}
                  ${isExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}
                `}
              >
                <div className="pl-1 pb-4 space-y-0">
                  {stage.services.map((service) => (
                    <Link
                      key={service.href}
                      to={service.href}
                      onClick={onNavigate}
                      className="block min-h-[48px] py-3 text-foreground hover:text-primary transition-colors"
                    >
                      {service.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* View All Services Link */}
      <div className="pt-2">
        <Link
          to="/services"
          onClick={onNavigate}
          className="flex items-center gap-1 py-3 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
        >
          View all services
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
};

export default MobileServicesNav;
